/**
 * Calendar-date helpers.
 *
 * The domain works with *calendar dates* (no time, no timezone): an attendance
 * entry belongs to "2026-01-15", never to an instant. To avoid the classic
 * off-by-one bugs of JS `Date`, every function here works on UTC midnight and
 * exposes dates as ISO strings `YYYY-MM-DD`.
 */
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** Parse "YYYY-MM-DD" into a UTC-midnight Date. Throws on malformed or impossible dates. */
export function parseIsoDate(value) {
    const match = ISO_DATE_RE.exec(value);
    if (!match)
        throw new Error(`Invalid ISO date: "${value}"`);
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const date = new Date(Date.UTC(year, month - 1, day));
    const roundTrips = date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
    if (!roundTrips)
        throw new Error(`Impossible calendar date: "${value}"`);
    return date;
}
/** Format a UTC-midnight Date as "YYYY-MM-DD". */
export function toIsoDate(date) {
    const y = date.getUTCFullYear().toString().padStart(4, '0');
    const m = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const d = date.getUTCDate().toString().padStart(2, '0');
    return `${y}-${m}-${d}`;
}
/** Build an ISO date from its parts (month is 1-based). */
export function isoDate(year, month, day) {
    return toIsoDate(new Date(Date.UTC(year, month - 1, day)));
}
/** The calendar day in Italy at a given instant: the day the users see. */
export function italianToday(now = new Date()) {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Rome',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(now);
}
/** Add (or subtract) whole days to an ISO date. */
export function addDays(value, days) {
    const date = parseIsoDate(value);
    date.setUTCDate(date.getUTCDate() + days);
    return toIsoDate(date);
}
/**
 * ISO weekday: 1 = Monday … 7 = Sunday.
 * (JS `getUTCDay()` returns 0 for Sunday; we normalise to the ISO convention used
 * by the payroll consultant and by the weekly overtime rule.)
 */
export function isoWeekday(value) {
    const jsDay = parseIsoDate(value).getUTCDay();
    return jsDay === 0 ? 7 : jsDay;
}
/** Monday of the ISO week containing the given date. */
export function startOfIsoWeek(value) {
    return addDays(value, 1 - isoWeekday(value));
}
function assertYearMonth(year, month) {
    if (!Number.isInteger(year) || year < 1900 || year > 2999)
        throw new Error(`Invalid year: ${year}`);
    if (!Number.isInteger(month) || month < 1 || month > 12)
        throw new Error(`Invalid month: ${month}`);
}
/** Number of days of a month (month is 1-based). */
export function daysInMonth(year, month) {
    assertYearMonth(year, month);
    // Day 0 of the next month is the last day of this one.
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
/** Every date of a month, in calendar order. */
export function datesOfMonth(year, month) {
    return Array.from({ length: daysInMonth(year, month) }, (_, index) => isoDate(year, month, index + 1));
}
//# sourceMappingURL=dates.js.map